"use client";

import { useRef, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import {
  Stack,
  Text,
  Loader,
  Button,
  TbRefresh,
  TbAlertCircle,
} from "@forestsync/ui";

interface GeoTiffViewerProps {
  url: string;
  type: "biomass" | "biomass-change" | "sentinel";
  onLoad?: () => void;
  onError?: (error: Error) => void;
  className?: string;
}

/**
 * Displays the rendered preview of a delivered imagery layer.
 *
 * The delivered GeoTIFFs are styled RGBA renders (colours, not measurements), so no
 * per-pixel values are read or shown — doing so would present colour intensities
 * as Mg/ha. The source .tif stays archived alongside the preview.
 */
export function GeoTiffViewer({
  url,
  type,
  onLoad,
  onError,
  className = "",
}: GeoTiffViewerProps) {
  const t = useTranslations("sink.imagery");
  const imgRef = useRef<HTMLImageElement>(null);

  // Convention: .tif -> -preview.png
  const previewUrl = url.replace(/\.tif$/i, "-preview.png");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    const errorMsg = t("loadError");
    setError(errorMsg);
    setIsLoading(false);
    if (onError) onError(new Error(errorMsg));
  }, [onError, t]);

  return (
    <div className={`relative h-full ${className}`}>
      <div className="relative w-full h-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
        <img
          ref={imgRef}
          src={previewUrl}
          alt={t("altText", { type })}
          className="w-full h-full object-contain [image-rendering:pixelated]"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
            <Stack gap="md" align="center">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">
                {t("loading")}
              </Text>
            </Stack>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
            <Stack gap="md" align="center" className="max-w-md p-4">
              <TbAlertCircle size={48} className="text-red-500" />
              <Text size="lg" fw={600} c="red">
                {t("errorTitle")}
              </Text>
              <Text size="sm" c="dimmed" className="text-center">
                {error}
              </Text>
              <Button
                leftSection={<TbRefresh size={16} />}
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  if (imgRef.current) imgRef.current.src = previewUrl;
                }}
              >
                {t("retry")}
              </Button>
            </Stack>
          </div>
        )}
      </div>
    </div>
  );
}
